/**
 * @file resolveAttachmentDisplayName.ts
 * @description Turns an attachment's stored file name into the name the panel
 * prints, aliasing the opaque `raw-email-<message-id>.json` of an ingested
 * email into `<form>-v<n>-email.json`. Consumers: `Attachments`,
 * `AttachmentList` and `AttachmentVersionRow`.
 *
 * `resolveCurrentAttachmentName` reads the artifact's authoritative
 * `fileName` first and falls back to the current version's `versionFileName`,
 * which is stamped at commit and still reads as the old name after a rename.
 * Where a version came from is `resolveAttachmentVersionOrigin`'s job, not this
 * module's.
 */

import type { ArtifactAttachments } from '#types/System.types';
import type { RawEmailAliasContext } from './Attachments';

const RAW_EMAIL_FILE_NAME_RE = /^raw-email-.+\.json$/i;
const RAW_EMAIL_ALIAS = 'original-email.json';
const EMAIL_ORIGIN_FORM_RE = /^forms\/([^/]+)\/formVersions\/([^/#]+)#EMAIL#/i;

export const resolveCurrentAttachmentName = (
    attachment: ArtifactAttachments[string] | undefined,
): string | undefined => {
    if (!attachment) return undefined;
    const latestVersion = attachment.currentVersionId
        ? attachment.versions?.[attachment.currentVersionId]
        : undefined;
    const name = attachment.fileName?.trim() || latestVersion?.versionFileName?.trim();
    return name || undefined;
};

export const resolveAttachmentDisplayName = (
    fileName: string | undefined,
    emailOriginRecordId?: string,
    rawEmailAliasContext?: RawEmailAliasContext,
): string => {
    if (typeof fileName !== 'string' || fileName.trim().length === 0) {
        return '';
    }
    const normalizedFileName = fileName.trim();
    if (!RAW_EMAIL_FILE_NAME_RE.test(normalizedFileName)) {
        return normalizedFileName;
    }
    const formNumber = rawEmailAliasContext?.formNumber?.trim();
    const versionNumber = rawEmailAliasContext?.formVersionNumber;
    if (formNumber && typeof versionNumber === 'number' && Number.isFinite(versionNumber)) {
        return `${formNumber}-v${versionNumber}-email.json`;
    }
    const match = typeof emailOriginRecordId === 'string'
        ? emailOriginRecordId.match(EMAIL_ORIGIN_FORM_RE)
        : null;
    if (match == null) {
        return RAW_EMAIL_ALIAS;
    }
    const [, recordFormNumber, recordVersion] = match;
    return `${recordFormNumber}-${recordVersion}-email.json`;
};
